<script>
  import { Link } from '@inertiajs/svelte'

  // Renders the "Stored in" containment spine from an ordered chain:
  //   [{ kind: 'property'|'space'|'container', label, href, direct }]
  // outermost first. Exactly one entry is `direct` (the deepest location).
  // href is present only for levels with a viewable page (property, container).
  let { chain = [] } = $props()

  function levelLabel(kind) {
    return kind === 'property' ? 'Property' : kind === 'space' ? 'Space' : 'Container'
  }
</script>

<div class="spine">
  {#snippet rowInner(level)}
    <span class="tile">
      {#if level.kind === 'property'}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9.5L12 3l9 6.5"/><path d="M5 10v10a1 1 0 001 1h12a1 1 0 001-1V10"/><path d="M9 21v-6h6v6"/></svg>
      {:else if level.kind === 'space'}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18"/><path d="M5 21V5a2 2 0 012-2h7a2 2 0 012 2v16"/><path d="M16 21V8h3a1 1 0 011 1v12"/><circle cx="12" cy="12" r="1"/></svg>
      {:else if level.direct}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8l-9-5-9 5 9 5 9-5z"/><path d="M3 8v8l9 5 9-5V8"/><path d="M12 13v8"/></svg>
      {:else}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9h18"/><path d="M9 4v5"/><path d="M15 4v5"/></svg>
      {/if}
    </span>
    <span class="txt">
      <span class="lbl">{levelLabel(level.kind)}</span>
      <span class="nm">{level.label}</span>
    </span>
    {#if level.href}
      <span class="go">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
      </span>
    {/if}
  {/snippet}

  {#each chain as level, depth (depth)}
    {@const cls = `row depth-${Math.min(depth, 3)} ${level.direct ? 'direct' : ''} ${level.href ? 'is-link' : 'static'}`}
    {#if level.href}
      <Link href={level.href} class={cls} title={`View all items in this ${level.kind}`}>
        {@render rowInner(level)}
      </Link>
    {:else}
      <div class={cls}>
        {@render rowInner(level)}
      </div>
    {/if}
  {/each}
</div>

<style>
  .spine :global(.row) {
    position: relative; display: flex; align-items: center; gap: 11px;
    padding: 9px 12px; border-radius: 11px; margin-bottom: 8px;
    border: 1px solid #e5e7eb; background: #f9fafb;
    text-decoration: none; color: inherit;
    transition: background .15s, border-color .15s, transform .1s;
  }
  .spine :global(.row:last-child) { margin-bottom: 0; }
  .spine :global(a.row.is-link:hover) { background: #f3f4f6; border-color: #d1d5db; transform: translateY(-1px); }
  .spine :global(.row.direct) { background: var(--color-brand-50); border-color: var(--color-brand-200); }
  .spine :global(a.row.direct.is-link:hover) { background: var(--color-brand-100); border-color: var(--color-brand-300); }

  .spine :global(.depth-1) { margin-left: 26px; }
  .spine :global(.depth-2) { margin-left: 52px; }
  .spine :global(.depth-3) { margin-left: 78px; }

  .spine :global(.depth-1)::before,
  .spine :global(.depth-2)::before,
  .spine :global(.depth-3)::before {
    content: ""; position: absolute; left: -19px; top: -10px; bottom: 50%; width: 16px;
    border-left: 2px solid #d1d5db; border-bottom: 2px solid #d1d5db;
    border-bottom-left-radius: 8px;
  }
  .spine :global(.row.direct.depth-1)::before,
  .spine :global(.row.direct.depth-2)::before,
  .spine :global(.row.direct.depth-3)::before { border-color: var(--color-brand-300); }

  .spine :global(.tile) {
    width: 34px; height: 34px; flex-shrink: 0; border-radius: 9px;
    display: grid; place-items: center; background: white;
    border: 1px solid #e5e7eb; color: #6b7280;
  }
  .spine :global(.tile svg) { width: 18px; height: 18px; }
  .spine :global(.row.direct .tile) { border-color: var(--color-brand-200); color: var(--color-brand-700); }

  .spine :global(.txt) { display: flex; flex-direction: column; min-width: 0; flex: 1; }
  .spine :global(.lbl) { font-size: 10.5px; text-transform: uppercase; letter-spacing: .05em; font-weight: 700; color: #6b7280; }
  .spine :global(.nm) { font-size: 14px; font-weight: 700; color: #111827; }
  .spine :global(a.row .nm) { color: #1f2937; }
  .spine :global(.row.direct .nm) { color: var(--color-brand-800); }
  .spine :global(.go) { flex-shrink: 0; color: #9ca3af; }
  .spine :global(.go svg) { width: 16px; height: 16px; }
  .spine :global(.row.direct .go) { color: var(--color-brand-600); }
</style>
